import { of as observableOf } from 'rxjs';

import { withLatestFrom, switchMap, map, catchError, skipWhile, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import * as actions from '../actions/index';
import { getSelectedId } from '../reducers/index';
import * as selectors from '../reducers/index';
import { CategoriesService } from '@careapp-group/ng-sdk';
import { getFilesUploading } from '../file-upload/file-upload-selectors';

@Injectable()
export class MemoCategoryEffects {
  getMemoCategories = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemoCategories.REQUEST_ALL),
      switchMap((action) => {
        return this.categoryService.listCategories({}).pipe(
          map((res) => actions.MemoCategories.ResponseSuccessAllAction(res)),
          catchError((err) => observableOf(actions.MemoCategories.ResponseFailAllAction(err))),
        );
      }),
    ),
  );

  fetchOrgMemoCategories = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemoCategories.REQUEST_BY_ID),
      withLatestFrom(this.store.select(getSelectedId(selectors.ORGANISATIONS))),
      switchMap(([action, organisationId]) => {
        return this.categoryService.listCategories({ organisationId }).pipe(
          map((data) =>
            actions.MemoCategories.ResponseSuccessByOrgIdAction({
              id: organisationId,
              data,
            }),
          ),
          catchError((err) => observableOf(actions.MemoCategories.ResponseFailByIdAction(err))),
        );
      }),
    ),
  );

  removeOrgMemoCategory = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemoCategories.REQUEST_REMOVE_BY_ID),
      withLatestFrom(this.store.select(getSelectedId(selectors.ORGANISATIONS))),
      switchMap(([action, organisationId]) => {
        return this.categoryService
          .removeFromOrganisation({ organisationId, categoryId: action.payload.id })
          .pipe(
            map((_) =>
              actions.MemoCategories.ResponseSuccessRemoveById({
                organisationId,
                categoryId: action.payload.id,
              }),
            ),
            catchError((err) => observableOf(actions.MemoCategories.ResponseFailRemoveById(err))),
          );
      }),
    ),
  );

  addOrgMemoCategory = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemoCategories.REQUEST_ADD_BY_ID),
      withLatestFrom(this.store.select(getSelectedId(selectors.ORGANISATIONS))),
      switchMap(([action, organisationId]) => {
        return this.categoryService
          .addToOrganisation({
            organisationId,
            categoryId: action.payload.id,
            ordinal: action.payload.ordinal,
          })
          .pipe(
            map((res) =>
              actions.MemoCategories.ResponseSuccessAddById({
                organisationId,
                data: res,
              }),
            ),
            catchError((err) => observableOf(actions.MemoCategories.ResponseFailAddById(err))),
          );
      }),
    ),
  );

  saveCategory = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemoCategories.REQUEST_SAVE),
      switchMap((action) =>
        this.store.select(getFilesUploading).pipe(
          skipWhile((uploading) => uploading),
          take(1),
          withLatestFrom(this.store.select(selectors.getFileUploadState)),
          switchMap(([uploading, files]) => {
            const animationId = action.payload.data.animationId
              ? (files[action.payload.data.animationId]?.response?.id ??
                action.payload.data.animationId)
              : null;

            return this.categoryService
              .updateCategory({
                id: action.payload.id,
                body: { ...action.payload.data, animationId },
              })
              .pipe(
                map((res) => actions.MemoCategories.ResponseSuccessSaveAction(res)),
                catchError((err) =>
                  observableOf(actions.MemoCategories.ResponseFailSaveAction(err)),
                ),
              );
          }),
        ),
      ),
    ),
  );

  createCategory = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemoCategories.REQUEST_CREATE),
      switchMap((action) =>
        this.store.select(getFilesUploading).pipe(
          skipWhile((uploading) => uploading),
          take(1),
          withLatestFrom(this.store.select(selectors.getFileUploadState)),
          switchMap(([uploading, files]) => {
            const animationId = action.payload.animationId
              ? (files[action.payload.animationId]?.response?.id ?? null)
              : null;

            return this.categoryService
              .createCategory({
                body: {
                  ...action.payload,
                  animationId,
                },
              })
              .pipe(
                map((res) => actions.MemoCategories.ResponseSuccessCreateAction(res)),
                catchError((err) =>
                  observableOf(actions.MemoCategories.ResponseFailCreateAction(err)),
                ),
              );
          }),
        ),
      ),
    ),
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<actions.CAAction>,
    private categoryService: CategoriesService,
  ) {}
}
